import { useState } from "react";
import * as XLSX from "xlsx";
import { submitFile } from "../services/magento/api.js";
import "./SalesDashboard.css";
import { normalizeRows } from "./rowNormalizer.js";
import { parseProfitMakerOJ, parseProfitMakerSJ } from "./ProfitMaker/profitMakerParser.js";


export default function SalesDashboard() {
  const [context, setContext] = useState("NETSUITE_SO_WIP");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError("");
    setStatus("");
    setProgress(0);
  };

  const handleSubmit = async () => {
    if (!file) {
        setError("Please select an Excel file.");
        return;
    }

    try {
        setLoading(true);
        setError("");
        setStatus("Reading file...");
        setProgress(0);

        // Read file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // Convert sheet to 2D array first
        const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        if (context === "PROFITMAKER_OJ") {
          const parsedRows = parseProfitMakerOJ(
            allRows,
            file.name
          );

          if (!parsedRows.length) {
            throw new Error("No valid PROFITMAKER_OJ records found.");
          }

          setStatus("Uploading batches...");

          await submitFile({
            context: "PROFITMAKER_OJ",
            records: parsedRows,
            onProgress: (current, total) => {
              const percent = Math.round((current / total) * 100);
              setProgress(percent);
              setStatus(`Uploading batch ${current} of ${total}`);
            }
          });

          setStatus("✅ Upload completed successfully!");
          return;
        }

        if (context === "PROFITMAKER_SJ") {
          const parsedRows = parseProfitMakerSJ(
            allRows,
            file.name
          );

          if (!parsedRows.length) {
            throw new Error("No valid PROFITMAKER_SJ records found.");
          }

          setStatus("Uploading batches...");

          await submitFile({
            context: "PROFITMAKER_SJ",
            records: parsedRows,
            onProgress: (current, total) => {
              const percent = Math.round((current / total) * 100);
              setProgress(percent);
              setStatus(`Uploading batch ${current} of ${total}`);
            }
          });

          setStatus("✅ Upload completed successfully!");
          return;
        }

        let headerRowIndex;
        if (context === "NETSUITE_SO_WIP") {
          headerRowIndex = allRows.findIndex(row =>
              row.includes("Document Number") &&
              row.includes("Customer") &&
              row.includes("Status")
          );
        } else if (context === "NETSUITE_SO_AR") {
          headerRowIndex = allRows.findIndex(row =>
              row.includes("Document Number") &&
              row.includes("Transaction Type") &&
              row.includes("Open Balance")
          );
        } else if (context === "FACILIS_SJ") {
          headerRowIndex = allRows.findIndex(row =>
              row.includes("Invoice #") &&
              row.includes("Invoice Date") &&
              row.includes("Committed")
          );
        } else if (context === "FACILIS_OJ") {
          headerRowIndex = allRows.findIndex(row =>
              row.includes("Job ID") &&
              row.includes("Company") &&
              row.includes("Date")
          );
        }

        if (headerRowIndex === -1) {
        throw new Error(`Could not find valid header row for ${context} in Excel file.`);
        }

        const keyColumnByContext = {
          NETSUITE_SO_WIP: "Document Number",
          NETSUITE_SO_AR: "Document Number",
          FACILIS_SJ: "Invoice #",
          FACILIS_OJ: "Job ID"
        };

        const headers = allRows[headerRowIndex].map(h =>
          h?.toString().replace(/\s+/g, " ").trim()
        );

        const keyColumn = keyColumnByContext[context];

        const keyIndex = headers.indexOf(keyColumn);

        if (keyIndex === -1) {
          throw new Error(`Key column '${keyColumn}' not found for ${context}`);
        }

        // Build objects using the correct header row
        const rawRows = allRows
          .slice(headerRowIndex + 1)
          .filter(row => row[keyIndex]) // ignore empty / totals
          .map(row => {
            const obj = {};
            headers.forEach((h, i) => {
              obj[h] = row[i] ?? "";
            });
            return obj;
          });

        if (!rawRows.length) {
        throw new Error(`No valid rows found in Excel file for ${context}.`);
        }

        // Normalize rows using your normalizer
        const normalizedRows = normalizeRows(context, rawRows);

        if (!normalizedRows.length) {
        throw new Error(`No valid ${context} records found. Check column structure.`);
        }

        setStatus("Uploading batches...");

        await submitFile({
        context,
        records: normalizedRows,
        onProgress: (current, total) => {
            const percent = Math.round((current / total) * 100);
            setProgress(percent);
            setStatus(`Uploading batch ${current} of ${total}`);
        },
        });

        setStatus("✅ Upload completed successfully!");
    } catch (err) {
        console.error(err);
        setError(err.message || "Upload failed.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="sales-dashboard">
      <div className="card">
        <h1>Sales Dashboard Upload</h1>

        <label>Context</label>
        <select
          value={context}
          onChange={(e) => setContext(e.target.value)}
          disabled={loading}
        >
          <option value="NETSUITE_SO_WIP">NETSUITE_SO_WIP</option>
          <option value="NETSUITE_SO_AR">NETSUITE_SO_AR</option>
          <option value="FACILIS_SJ">FACILIS_SJ</option>
          <option value="FACILIS_OJ">FACILIS_OJ</option>
          <option value="PROFITMAKER_SJ">PROFITMAKER_SJ</option>
          <option value="PROFITMAKER_OJ">PROFITMAKER_OJ</option>
        </select>

        <label>Excel File</label>
        <input
          type="file"
          accept=".xls,.xlsx"
          onChange={handleFileChange}
          disabled={loading}
        />

        <button onClick={handleSubmit} disabled={loading}>
          {loading ? "Uploading..." : "Submit"}
        </button>

        {loading && (
          <div className="progress-wrapper">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span>{progress}%</span>
          </div>
        )}

        {status && <p className="status">{status}</p>}
        {error && <p className="error">❌ {error}</p>}
      </div>
    </div>
  );
}
